import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import configData from '../../config.json';
import { useDataContext } from '../../context/data';
import translations from '../../constants/translations.json';

// Guest discount code (DISCOUNT-CODES-SPEC.md §8.1/§8.2). Two exports, on
// purpose:
//
//   useDiscountCodeLookup — the debounced /discountcode/validate lookup. The
//     PAGE calls it, not this component (see the hook's own note).
//   DiscountCodeBlock     — the four-state field, purely presentational.
//
// Structurally the lookup is LoyaltyBanner.js — the repo's template for
// "validate something against the backend": the report callback lives in a ref
// so a fresh inline callback is not an effect dependency, the lookup is debounced
// 400 ms, and a `cancelled` flag drops stale responses.
//
// THE CLIENT NEVER COMPUTES THE DISCOUNT. It asks /discountcode/validate for a
// preview, sends only the code STRING with the order, and displays the server's
// number once the order comes back. The order array posted here is the one
// formatOrdersForCheckout builds for checkout and carries no prices at all — the
// server prices every line through the same helpers the order paths use, so the
// quote and the charge cannot diverge.
//
// The single most important behaviour copied from LoyaltyBanner: it ALWAYS
// reports a value, including 0. If the guest removes the qualifying item the
// discount has to drop to zero immediately, or the quote and the charge diverge.

// The backend formats money the same way (heywaitr-staging
// src/controller/discountCode.js:186-191). Keeping the two in step is what makes
// "du sparer 51,00 DKK" read identically whether the string came from our own
// table or from the server's `message`.
const DECIMAL_COMMA = ['da', 'no', 'sv', 'de'];

export const formatDiscountMoney = (value, language) => {
  const fixed = (Math.round((Number(value) || 0) * 100) / 100).toFixed(2);
  return DECIMAL_COMMA.indexOf(language) === -1 ? fixed : fixed.replace('.', ',');
};

// The BCP 47 tag behind each UI language, for date formatting.
const DATE_LOCALE = { da: 'da-DK', no: 'nb-NO', sv: 'sv-SE', de: 'de-DE', en: 'en-GB' };

/**
 * A server date (ISO `YYYY-MM-DD`) written the way the guest's locale writes it.
 * "Rabatkoden udløb 2026-07-27" reads as a machine string in a Danish sentence.
 *
 * The parts are read off the string and handed to the Date constructor as LOCAL
 * numbers deliberately: `new Date('2026-07-27')` parses as UTC midnight, which in
 * a negative-offset timezone renders as the 26th. An expiry date showing a day
 * early is exactly the kind of thing a guest would rightly complain about.
 */
export const formatDiscountDate = (value, language) => {
  const raw = String(value == null ? '' : value);
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return raw;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(d.getTime())) return raw;
  try {
    return d.toLocaleDateString(DATE_LOCALE[language] || 'en-GB');
  } catch (e) {
    return raw;
  }
};

// Currency is deliberately NOT rewritten to "kr". The whole app names money by
// its ISO code — item rows, tips, delivery, the discount row and the total all
// read "DKK 95.00" — so a message saying "95,00 kr" directly beneath a total
// saying "DKK 95.00" would be the only place in the checkout that disagrees with
// itself. Changing all of it is a decision about existing UI, not this feature's
// to make on the way past.

/**
 * The localized guest copy for one server answer.
 *
 * §4.6: the client carries its own copy of the §8.5 table and PREFERS it, so a
 * missing or odd Accept-Language on the request can never show a Danish guest an
 * English error. The server's own `message` is the fallback for a message key
 * this build does not know yet.
 *
 * The three money placeholders are formatted exactly like the backend does it
 * (formatMetaForMessage), the rest are substituted verbatim. A placeholder whose
 * key is absent from `meta` is left in place — a visible {code} in a bug report
 * is worth far more than a silently empty sentence.
 */
export const discountMessage = (language, messageKey, meta, serverMessage) => {
  const template = messageKey ? translations[language]?.[messageKey] : '';
  if (!template) return serverMessage || '';

  const values = { ...(meta || {}) };
  ['amount', 'missing', 'minOrderValue'].forEach((key) => {
    if (values[key] !== null && values[key] !== undefined && isFinite(Number(values[key]))) {
      values[key] = formatDiscountMoney(values[key], language);
    }
  });
  if (values.date !== null && values.date !== undefined) {
    values.date = formatDiscountDate(values.date, language);
  }

  return template.replace(/\{(\w+)\}/g, (placeholder, name) =>
    values[name] === null || values[name] === undefined ? placeholder : String(values[name]),
  );
};

// A stable empty basket, so a caller that omits `order` does not hand the effect
// a new array identity on every render.
const EMPTY_ORDER = [];

// BACKWARDS COMPATIBILITY (this branch only, not on master).
//
// production runs against an API that may not carry /discountcode/validate yet.
// Master has no such case — it is developed against the staging backend, where
// the route always exists — so there a 404 falls into the generic catch and the
// guest is told "we couldn't check that code right now", forever, with no way to
// clear it and a dead field left on the checkout.
//
// Latched module-wide rather than per-component: the answer is a property of the
// deployment, not of a render, and one probe per page load is enough. Once set,
// the field reports `unsupported` and both pages stop rendering it — the same
// checkout the guest sees today, minus the input that cannot work. It resets on
// reload, so deploying the backend later needs no client change.
let endpointUnsupported = false;

/**
 * The debounced /discountcode/validate lookup — call this from the PAGE.
 *
 * It deliberately does NOT live inside DiscountCodeBlock. The field is rendered
 * by OrderInformation inside `ReviewStep`, and React Router renders only the
 * matching route, so the whole review subtree unmounts the instant the guest
 * steps on to `payment`. `YourItems` does not: it sits outside <Routes> and is on
 * screen on both steps, with live −/+ quantity buttons. An effect that unmounted
 * with the review step would stop watching the cart at exactly the point where
 * the guest can still change it, leaving the server-quoted amount pinned to a
 * basket that no longer exists — the quote/charge divergence §8.2 calls the
 * single most important thing to get right. It is also what carries a code
 * restored after a MobilePay redirect (which comes back on `payment`) through
 * re-validation. LoyaltyBanner solves the same problem by being mounted in both
 * YourItems.js and Payment.js; from the page, one instance covers every step.
 *
 * Returns `locked` — true only while a check the guest ASKED for (Apply / Enter)
 * is in flight, so the field can be disabled for that and only that.
 *
 * It takes `restaurant` and `guestInfo` whole and resolves the contact and the
 * currency itself — the LoyaltyBanner idiom, and here it is also load-bearing.
 * `pages/RestaurantMenu/index.js` sits on the eslint-plugin-react-hooks 4.3.0
 * cliff described in `utils/stampcard.js:43-48`; ONE more `||` or `?.` anywhere
 * in that component's own body tips its code-path analysis over and every hook
 * below ~line 2500 is reported as conditionally called, failing the CRA build.
 * Resolving in here keeps those branches inside this function instead.
 */
export const useDiscountCodeLookup = ({
  restaurantId,
  code = '',
  order = EMPTY_ORDER,
  mobilenumber = '',
  guestInfo,
  restaurant,
  isGuest = false,
  guestId = '',
  isDineIn = false,
  applyNonce = 0,
  onResult,
}) => {
  const { userProfile, language } = useDataContext();

  // Resolved to primitives so they can be stable effect dependencies: both
  // `guestInfo` and `restaurant` are fresh objects on most renders, which would
  // otherwise re-fire the effect every render and hammer the endpoint. The guest
  // checkout form wins over the signed-in profile, so the preview is priced for
  // whoever is actually ordering.
  const email =
    guestInfo?.email || userProfile?.profile?.email || userProfile?.profile?.Email || '';
  const currency = restaurant?.currency?.code || 'DKK';

  // The field is locked only while a check the guest ASKED for (Apply / Enter)
  // is in flight. Locking it for the background debounce check as well would
  // blur the input from under a guest who is still typing their code — they type
  // "SOMM", pause, the debounce fires, and the field goes dead mid-entry.
  const [locked, setLocked] = useState(false);

  // Keep the latest onResult callable without making it an effect dependency —
  // the page passes a fresh inline callback on most renders.
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  // Same reason: userProfile is re-parsed out of localStorage and is a fresh
  // object on most renders.
  const token = userProfile?.token || '';

  // The cart fingerprint. formatOrdersForCheckout builds a NEW array on every
  // render, so the array identity is useless as a dependency; its contents are
  // exactly what the server prices, so its JSON is the honest trigger.
  const fingerprint = JSON.stringify(order || EMPTY_ORDER);

  // Pressing Apply (or Enter) must not wait out the debounce.
  const lastApplyNonceRef = useRef(applyNonce);

  // The cart the last effect run saw, so a cart EDIT can be told apart from a
  // code edit or an identity edit.
  const lastFingerprintRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    // Every re-run means the code or the cart moved, so whatever was locked is
    // stale. Setting the same value is a no-op in React, so this cannot loop.
    setLocked(false);

    const report = (result) => {
      if (!cancelled && onResultRef.current) onResultRef.current(result);
    };

    const local = (reason, messageKey, typed) => ({
      status: 'error',
      valid: false,
      code: typed,
      amount: 0,
      reason,
      messageKey,
      message: discountMessage(language, messageKey, {}, ''),
    });

    const trimmed = String(code || '').trim();
    const items = JSON.parse(fingerprint);

    // Bookkeeping before any early return, so a run that answers locally still
    // records the basket it answered for.
    const cartChanged =
      lastFingerprintRef.current !== null && lastFingerprintRef.current !== fingerprint;
    lastFingerprintRef.current = fingerprint;

    // No code, no discount, no request — and the parent is told so, so a code
    // the guest just deleted cannot leave its amount behind on the total.
    if (!trimmed) {
      report({ status: 'idle', valid: false, code: '', amount: 0, message: '' });
      return undefined;
    }

    // An empty basket has nothing to price. Answering locally costs the server
    // nothing and uses the same locked string it would have returned.
    if (!restaurantId || !items.length) {
      report(local('empty_cart', 'discount_code_error_empty_cart', trimmed));
      return undefined;
    }

    // This deployment's API has no discount-code route (latched on the first
    // 404/501 below). Report it so the page hides the field, and never ask again.
    if (endpointUnsupported) {
      report({ status: 'unsupported', valid: false, code: trimmed, amount: 0, message: '' });
      return undefined;
    }

    // A cart edit makes the server's last answer stale on the spot. Drop the
    // amount BEFORE the debounce rather than after the round trip, so the guest
    // can never press Pay during that ~600 ms window against a total that still
    // carries a discount priced for the previous basket. Only a cart edit does
    // this: a code edit already clears the amount on every keystroke (the page's
    // handleDiscountCodeChange), and an identity edit cannot change the amount —
    // at worst it invalidates the code, which the order path rejects loudly.
    if (cartChanged) {
      report({ status: 'checking', valid: false, code: trimmed, amount: 0, message: '' });
    }

    const fromServer = (data) => {
      if (data.valid === true) {
        const appliedCurrency = data.currency || currency;
        return {
          status: 'applied',
          valid: true,
          code: data.code || trimmed,
          amount: Number(data.discountAmount) || 0,
          type: data.discountType || null,
          value: Number(data.discountValue) || 0,
          currency: appliedCurrency,
          remainingUses: data.remainingUses,
          messageKey: data.messageKey || 'discount_code_applied',
          message: discountMessage(
            language,
            data.messageKey || 'discount_code_applied',
            {
              code: data.code || trimmed,
              amount: data.discountAmount,
              currency: appliedCurrency,
            },
            data.message,
          ),
        };
      }

      return {
        status: 'error',
        valid: false,
        code: data.code || trimmed,
        amount: 0,
        reason: data.reason || 'unavailable',
        messageKey: data.messageKey || null,
        message: discountMessage(language, data.messageKey, data.meta, data.message),
      };
    };

    const run = async (locking) => {
      if (locking) setLocked(true);
      report({ status: 'checking', valid: false, code: trimmed, amount: 0, message: '' });

      try {
        const response = await axios({
          method: 'POST',
          url: `${configData.SERVER_URL}/discountcode/validate`,
          // BACKWARDS COMPATIBILITY (this branch only). Both checkout pages
          // refuse to leave the review step while a check is in flight, so a
          // request that never settles would strand the guest on review with no
          // way forward. Bounding it turns a hang into the ordinary "we couldn't
          // check that code" error, which the guest can clear and order past.
          timeout: 15000,
          data: {
            restaurantId,
            code: trimmed,
            order: items,
            mobilenumber,
            email,
            isGuest,
            guestId,
            isDineIn,
          },
          // The token is not optional here (§8 — every call in this repo is a
          // bare axios carrying it). The endpoint resolves the caller through
          // MenuController.getUserId(req), and that one value decides two
          // things: whether the basket is priced at heywaitr+ member prices
          // (controller/discountCode.js — `if(_menu.DiscountEnabled &&
          // isSubscribed)`) and which per-guest cap key the redemption ledger is
          // probed under. The menu itself is already fetched with the token, so
          // an anonymous preview would price the very same basket at list price
          // and quote a discount the order path never charges.
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (cancelled) return;
        report(fromServer(response?.data || {}));
      } catch (e) {
        if (cancelled) return;

        // The preview answers 200 for every ordinary rejection precisely so a
        // typo does not read as a network failure (§4.4). The two exceptions —
        // 429 rate_limited and 503 unavailable — carry the very same envelope,
        // so read it instead of inventing copy.
        const data = e?.response?.data;
        if (data && data.valid === false) {
          report(fromServer(data));
          return;
        }

        // BACKWARDS COMPATIBILITY (this branch only). An API that predates
        // discount codes has no /discountcode/validate: Express answers 404, a
        // gateway in front of it may answer 501. Neither is a code the guest can
        // do anything about, and the generic "try again" below would invite them
        // to retry a route that will never exist. Latch it and hide the field.
        // Checked AFTER the envelope above so a real 404 FROM the endpoint —
        // { valid: false, reason: 'not_found' } — is still read as a bad code.
        const httpStatus = e?.response?.status;
        if (httpStatus === 404 || httpStatus === 501) {
          endpointUnsupported = true;
          report({ status: 'unsupported', valid: false, code: trimmed, amount: 0, message: '' });
          return;
        }

        console.log('Discount code check:', e.message);
        report(local('unavailable', 'discount_code_error_unavailable', trimmed));
      } finally {
        if (!cancelled) setLocked(false);
      }
    };

    const immediate = lastApplyNonceRef.current !== applyNonce;
    lastApplyNonceRef.current = applyNonce;

    // Debounce so a guest typing a code triggers at most one lookup once the
    // input settles, not one per keystroke. Apply/Enter skips the wait — and is
    // the only trigger that locks the field, because it is the only one the
    // guest made deliberately.
    const timer = setTimeout(() => run(immediate), immediate ? 0 : 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // onResult is intentionally read via ref, not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    restaurantId,
    code,
    fingerprint,
    mobilenumber,
    email,
    isGuest,
    guestId,
    isDineIn,
    applyNonce,
    language,
    token,
  ]);

  return locked;
};

const DiscountCodeBlock = ({
  code = '',
  onCodeChange,
  onApply,
  onRemove,
  currency = 'DKK',
  locked = false,
  status = 'idle',
  amount = 0,
  error = '',
}) => {
  const { language } = useDataContext();
  const t = (key, fallback) => translations[language]?.[key] || fallback;

  // BACKWARDS COMPATIBILITY (this branch only): the API on this deployment has
  // no discount-code route. Render nothing rather than a field that can only
  // fail — the checkout then looks exactly as it does in production today.
  if (status === 'unsupported') return null;

  const isChecking = status === 'checking';
  const isApplied = status === 'applied' && Number(amount) > 0;
  const isError = status === 'error' && Boolean(error);

  return (
    <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 space-y-4">
      <label className="block text-sm lg:text-base font-bold">
        {t('order_information_discount_label', 'Rabatkode')}
      </label>

      {isApplied ? (
        <div className="discount-applied-pill">
          <span className="text-sm font-bold">
            ✓ {code} · −{formatDiscountMoney(amount, language)} {currency}
          </span>
          <button
            type="button"
            className="discount-applied-remove"
            aria-label={t('discount_code_remove', 'Fjern')}
            onClick={onRemove}
          >
            ×
          </button>
        </div>
      ) : (
        <div className="relative space-x-2">
          <input
            type="text"
            className={`input-discount text-sm w-full px-4 py-[14px] !pr-[175px] border rounded-full${
              isError ? ' is-error' : ''
            }`}
            placeholder={translations[language]?.order_information_discount_placeholder}
            value={code}
            disabled={locked}
            onChange={(e) => onCodeChange(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // The field sits inside the checkout form; Enter must apply the
                // code, never submit whatever the browser considers the form.
                e.preventDefault();
                onApply();
              }
            }}
          />
          <button
            type="button"
            className="absolute top-1/2 right-[5px] -translate-y-1/2 bg-primary hover:bg-secondary transition-colors text-sm text-white font-bold px-8 py-[10px] rounded-full disabled:opacity-60"
            disabled={isChecking}
            onClick={onApply}
          >
            {isChecking ? (
              <>
                <i className="fa fa-spinner fa-spin" /> {t('discount_code_checking', 'Tjekker …')}
              </>
            ) : (
              translations[language]?.order_information_discount_button
            )}
          </button>
        </div>
      )}

      {isError && <p className="text-xs lg:text-sm text-[#ef4444]">{error}</p>}
    </div>
  );
};

export default DiscountCodeBlock;
